import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../utils/cn";

import { toggleVariants } from "./toggle";

const ToggleGroupContext = React.createContext<
    VariantProps<typeof toggleVariants> & {
        spacing?: number;
        orientation?: "horizontal" | "vertical";
    }
>({
    size: "default",
    variant: "default",
    spacing: 0,
    orientation: "horizontal"
});

function ToggleGroup({
    className,
    variant,
    size,
    spacing = 0,
    orientation = "horizontal",
    children,
    ...props
}: ToggleGroupPrimitive.Props &
    VariantProps<typeof toggleVariants> & {
        spacing?: number;
        orientation?: "horizontal" | "vertical";
    }): React.ReactElement {
    return (
        <ToggleGroupPrimitive
            data-slot="toggle-group"
            data-variant={variant}
            data-size={size}
            data-spacing={spacing}
            data-orientation={orientation}
            style={{ "--gap": String(spacing) } as React.CSSProperties}
            className={cn(
                // `calc(var(--gap) * 0.25rem)` mirrors shadcn's `--spacing(var(--gap))`
                // — spacing=6 → 1.5rem, matching Tailwind's default spacing scale.
                "group/toggle-group flex w-fit flex-row items-center gap-[calc(var(--gap)*0.25rem)] rounded-none",
                "data-[size=sm]:rounded-sm",
                "data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch",
                className
            )}
            {...props}
        >
            <ToggleGroupContext.Provider
                value={{ variant, size, spacing, orientation }}
            >
                {children}
            </ToggleGroupContext.Provider>
        </ToggleGroupPrimitive>
    );
}

function ToggleGroupItem({
    className,
    children,
    variant = "default",
    size = "default",
    ...props
}: TogglePrimitive.Props &
    VariantProps<typeof toggleVariants>): React.ReactElement {
    const context = React.useContext(ToggleGroupContext);

    return (
        <TogglePrimitive
            data-slot="toggle-group-item"
            data-variant={context.variant || variant}
            data-size={context.size || size}
            data-spacing={context.spacing}
            className={cn(
                "shrink-0 focus:z-10 focus-visible:z-10",
                // When spacing=0, items form a joined pill — share borders, only
                // first/last get rounded corners, tighter padding when icon-only.
                "group-data-[spacing=0]/toggle-group:rounded-none group-data-[spacing=0]/toggle-group:px-2",
                "group-data-[spacing=0]/toggle-group:has-data-[icon=inline-end]:pr-1.5",
                "group-data-[spacing=0]/toggle-group:has-data-[icon=inline-start]:pl-1.5",
                "group-data-[orientation=horizontal]/toggle-group:group-data-[spacing=0]/toggle-group:first:rounded-l-sm",
                "group-data-[orientation=horizontal]/toggle-group:group-data-[spacing=0]/toggle-group:last:rounded-r-sm",
                "group-data-[orientation=vertical]/toggle-group:group-data-[spacing=0]/toggle-group:first:rounded-t-sm",
                "group-data-[orientation=vertical]/toggle-group:group-data-[spacing=0]/toggle-group:last:rounded-b-sm",
                // Outline variant: remove duplicated borders between adjacent items
                "group-data-[orientation=horizontal]/toggle-group:group-data-[spacing=0]/toggle-group:data-[variant=outline]:border-l-0",
                "group-data-[orientation=vertical]/toggle-group:group-data-[spacing=0]/toggle-group:data-[variant=outline]:border-t-0",
                "group-data-[orientation=horizontal]/toggle-group:group-data-[spacing=0]/toggle-group:data-[variant=outline]:first:border-l",
                "group-data-[orientation=vertical]/toggle-group:group-data-[spacing=0]/toggle-group:data-[variant=outline]:first:border-t",
                toggleVariants({
                    variant: context.variant || variant,
                    size: context.size || size
                }),
                className
            )}
            {...props}
        >
            {children}
        </TogglePrimitive>
    );
}

export { ToggleGroup, ToggleGroupItem };
